import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useConnection } from '@sezenta/connection';
import { useParams } from 'react-router-dom';
import { Progress, Spin } from 'antd';
import { useEventListener } from '../common/EventContext';
import { setLastContractId } from './ContractHelpers';
import {
  CreditCardType,
  MasterData,
  Permission,
  UserGroup,
  Vendor,
} from '../dto';
import { State } from '../dto/State';

export class Role {
  businessId: string;
  id: string;
  name: string;
}

export class Site {
  id: string;
  name: string;
  suburb: string;
  state: string;
  council: string;
  zone: string;
  area: string;
  costCenter?: string;
  address?: string;
}
export class SiteWithIds {
  id: number;
  name: string;
  suburb: number;
  state: number;
  council: number;
  zone: number;
  area: number;
  costCenter?: string;
  address?: string;
}
export interface MasterDataType {
  id: number;
  name: string;
  state?: string;
  weight?: number;
  status: string;
}

export type BusinessContextData = {
  businessId: string;
  // Re-fetch the business context (users / roles / sites / master data). Call after
  // changing master data elsewhere so dependent dropdowns refresh without a reload.
  reload: () => void;
  masterData: Record<string, MasterData[]>;
  select: Record<
    string,
    { label: string; value: string; name?: string; options?: any[] }[]
  >;
  roles: Record<string, Role>;
  states: Record<string, State>;
  sites: Record<string, Site>;
  sitesWithIds: Record<string, SiteWithIds>;
  zones: Record<string, MasterDataType>;
  area: Record<string, MasterDataType>;
  suburbs: Record<string, MasterDataType>;
  councils: Record<string, MasterDataType>;
  collectionType: Record<string, MasterDataType>;
  getNameById: (type: string, id: string) => string;
};

export const BusinessContext = React.createContext<BusinessContextData>(
  {} as any,
);

export const BusinessContextProvider: FC<PropsWithChildren> = (props) => {
  // CR004: the active contract comes from the `/portal/:contractId/...` URL — the
  // shareable source of truth — not a hardcoded '1'. Server TenantGuard cross-checks
  // it against the token on every call. Fall back to '1' for legacy/bare paths.
  const { contractId } = useParams<{ contractId: string }>();
  const businessId = contractId ?? '1';

  useEffect(() => {
    if (contractId) {
      setLastContractId(contractId);
    }
  }, [contractId]);
  const [roles, setRoles] = useState<Record<string, Role>>({});
  const [sites, setSites] = useState<Record<string, Site>>({});
  const [sitesWithIds, setSitesWithIds] = useState<Record<string, SiteWithIds>>(
    {},
  );
  const [select, setSelect] = useState<
    Record<string, { label: string; value: string; name?: any }[]>
  >({});
  const [loading, setLoading] = useState<boolean>(false);
  const connection = useConnection();
  const progressInterval = React.useRef<ReturnType<typeof setInterval>>();
  const [progress, setProgress] = useState<number>(0);
  const [masterData, setMasterData] = useState<Record<string, MasterData[]>>(
    {},
  );
  const [states, setStates] = useState<Record<string, State>>({});
  const [zones, setZones] = useState<Record<string, MasterDataType>>({});
  const [area, setArea] = useState<Record<string, MasterDataType>>({});
  const [councils, setCouncils] = useState<Record<string, MasterDataType>>({});
  const [suburbs, setSuburbs] = useState<Record<string, MasterDataType>>({});
  const [collectionType, setCollectionType] = useState<
    Record<string, MasterDataType>
  >({});

  // Function to get name by ID from master data
  const getNameById = useCallback(
    (type: string, id: string): string => {
      if (!id || !masterData[type]) return id;
      return (
        masterData[type].find(
          (item: MasterData) => Number(id) === Number(item.id),
        )?.name || id
      );
    },
    [masterData],
  );

  // Event listener to update master data when items are created or updated
  useEventListener(
    'MASTER_DATA',
    useCallback((evt) => {
      if ((evt?.action === 'CREATE' || evt?.action === 'UPDATE') && evt?.data) {
        const newItem = evt.data;
        const itemType = newItem.type;

        // Update the masterData collection
        setMasterData((prev) => {
          if (evt.action === 'CREATE') {
            return {
              ...prev,
              [itemType]: [...(prev[itemType] || []), newItem],
            };
          } else if (evt.action === 'UPDATE') {
            return {
              ...prev,
              [itemType]: (prev[itemType] || []).map((item) =>
                item.id === newItem.id ? newItem : item,
              ),
            };
          }
          return prev;
        });

        // Update the specific type collections (works for both CREATE and UPDATE)
        switch (itemType) {
          case 'State':
            setStates((prev) => ({
              ...prev,
              [newItem.id]: newItem,
            }));
            break;
          case 'Zone':
            setZones((prev) => ({
              ...prev,
              [newItem.id]: newItem,
            }));
            break;
          case 'Area':
            setArea((prev) => ({
              ...prev,
              [newItem.id]: newItem,
            }));
            break;
          case 'Suburb':
            setSuburbs((prev) => ({
              ...prev,
              [newItem.id]: newItem,
            }));
            break;
          case 'Council':
            setCouncils((prev) => ({
              ...prev,
              [newItem.id]: newItem,
            }));
            break;
          case 'CollectionType':
            setCollectionType((prev) => ({
              ...prev,
              [newItem.id]: newItem,
            }));
            break;
        }

        // Update the select options
        setSelect((prev) => {
          const newSelectItem = {
            name: newItem.name,
            value: newItem.id,
            label: newItem.name,
          };

          const updateSelectArray = (
            currentArray: any[],
            newItem: any,
            action: string,
            status: string,
          ) => {
            if (action === 'CREATE' && status === 'Active') {
              return [...currentArray, newItem].sort((a, b) =>
                a.name.localeCompare(b.name),
              );
            } else if (action === 'UPDATE') {
              const filteredArray = currentArray.filter(
                (item) => item.value !== newItem.value,
              );
              if (status === 'Active') {
                return [...filteredArray, newItem].sort((a, b) =>
                  a.name.localeCompare(b.name),
                );
              }
              return filteredArray;
            }
            return currentArray;
          };

          switch (itemType) {
            case 'State':
              return {
                ...prev,
                states: updateSelectArray(
                  prev.states || [],
                  newSelectItem,
                  evt.action,
                  newItem.status,
                ),
              };
            case 'Zone':
              return {
                ...prev,
                zones: updateSelectArray(
                  prev.zones || [],
                  newSelectItem,
                  evt.action,
                  newItem.status,
                ),
              };
            case 'Area':
              return {
                ...prev,
                areas: updateSelectArray(
                  prev.areas || [],
                  newSelectItem,
                  evt.action,
                  newItem.status,
                ),
              };
            case 'Suburb':
              return {
                ...prev,
                suburbs: updateSelectArray(
                  prev.suburbs || [],
                  newSelectItem,
                  evt.action,
                  newItem.status,
                ),
              };
            case 'Council':
              return {
                ...prev,
                councils: updateSelectArray(
                  prev.councils || [],
                  newSelectItem,
                  evt.action,
                  newItem.status,
                ),
              };
            case 'CollectionType':
              return {
                ...prev,
                collectionTypes: updateSelectArray(
                  prev.collectionTypes || [],
                  newSelectItem,
                  evt.action,
                  newItem.status,
                ),
              };
            default:
              return prev;
          }
        });
      }
    }, []),
  );

  useEffect(() => {
    if (loading) {
      setProgress(0);
      progressInterval.current = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 90) {
            clearInterval(progressInterval.current);
            return 90;
          }
          const increment = Math.max(1, (90 - prevProgress) / 10);
          return Math.min(90, prevProgress + increment);
        });
      }, 100);
    } else {
      // When loading is complete, quickly go to 100%
      setProgress(100);
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    }

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [loading]);

  // Re-fetchable so callers can refresh master data (e.g. after seeding zones /
  // collection types in another screen) without a full page reload. `silent` skips
  // the full-page loading spinner so a background refresh doesn't flash the portal.
  const loadContext = useCallback(
    (silent = false) => {
      if (!silent) setLoading(true);
      connection
        .get(`businesses/${businessId}/context`)
        .then((res) => {
          setMasterData(res.data.masterData);
          setRoles(
            Object.fromEntries(
              res.data.roles.map((item: Role) => [item.id, item]),
            ),
          );
          setStates(
            Object.fromEntries(
              [...(res.data.masterData['State'] ?? [])].map((item: State) => [
                item.id,
                item,
              ]),
            ),
          );
          setZones(
            Object.fromEntries(
              [...(res.data.masterData['Zone'] ?? [])].map(
                (item: MasterDataType) => [item.id, item],
              ),
            ),
          );
          setArea(
            Object.fromEntries(
              [...(res.data.masterData['Area'] ?? [])].map(
                (item: MasterDataType) => [item.id, item],
              ),
            ),
          );
          setSuburbs(
            Object.fromEntries(
              [...(res.data.masterData['Suburb'] ?? [])].map(
                (item: MasterDataType) => [item.id, item],
              ),
            ),
          );
          setCouncils(
            Object.fromEntries(
              [...(res.data.masterData['Council'] ?? [])].map(
                (item: MasterDataType) => [item.id, item],
              ),
            ),
          );
          setCollectionType(
            Object.fromEntries(
              [...(res.data.masterData['CollectionType'] ?? [])].map(
                (item: MasterDataType) => [item.id, item],
              ),
            ),
          );

          const getNameById = (type: string, id: string): string => {
            if (!id || !res.data.masterData[type]) return id;
            return (
              res.data.masterData[type].find(
                (item: MasterData) => item.id === id,
              )?.name || id
            );
          };

          setSites(
            Object.fromEntries(
              res.data.sites.map((item: Site) => [
                item.id,
                {
                  ...item,
                  suburb: getNameById('Suburb', item.suburb),
                  state: getNameById('State', item.state),
                  council: getNameById('Council', item.council),
                  zone: getNameById('Zone', item.zone),
                  area: getNameById('Area', item.area),
                  address: item.address,
                },
              ]),
            ),
          );

          setSitesWithIds(
            Object.fromEntries(
              res.data.sites.map((item: Site) => [
                item.id,
                {
                  ...item,
                  suburb: item.suburb,
                  state: item.state,
                  council: item.council,
                  zone: item.zone,
                  area: item.area,
                  address: item.address,
                },
              ]),
            ),
          );

          setSelect({
            roles: res.data.roles
              .sort((a: Role, b: Role) => a.name.localeCompare(b.name))
              .map((item: Role) => ({
                label: item.name,
                value: item.id,
                name: item.name,
              })),
            sites: res.data.sites
              .sort((a: Site, b: Site) => a.name.localeCompare(b.name))
              .map((item: Site) => ({
                label: item.name,
                value: item.id,
                name: item.name,
              })),
            areas: [...(res.data.masterData['Area'] ?? [])]
              .filter((v) => v.status === 'Active')
              .sort((a: MasterData, b: MasterData) =>
                a.name.localeCompare(b.name),
              )
              .map((item: MasterData) => ({
                name: item.name,
                value: item.id,
                label: item.name,
              })),
            suburbs: [...(res.data.masterData['Suburb'] ?? [])]
              .filter((v) => v.status === 'Active')
              .sort((a: MasterData, b: MasterData) =>
                a.name.localeCompare(b.name),
              )
              .map((item: MasterData) => ({
                name: item.name,
                value: item.id,
                label: item.name,
              })),
            states: [...(res.data.masterData['State'] ?? [])]
              .filter((v) => v.status === 'Active')
              .sort((a: MasterData, b: MasterData) =>
                a.name.localeCompare(b.name),
              )
              .map((item: MasterData) => ({
                name: item.name,
                value: item.id,
                label: item.name,
              })),
            councils: [...(res.data.masterData['Council'] ?? [])]
              .filter((v) => v.status === 'Active')
              .sort((a: MasterData, b: MasterData) =>
                a.name.localeCompare(b.name),
              )
              .map((item: MasterData) => ({
                name: item.name,
                value: item.id,
                label: item.name,
              })),
            zones: [...(res.data.masterData['Zone'] ?? [])]
              .filter((v) => v.status === 'Active')
              .sort((a: MasterData, b: MasterData) =>
                a.name.localeCompare(b.name),
              )
              .map((item: MasterData) => ({
                name: item.name,
                value: item.id,
                label: item.name,
              })),
            collectionTypes: [...(res.data.masterData['CollectionType'] ?? [])]
              .filter((v) => v.status === 'Active')
              .sort((a: MasterData, b: MasterData) =>
                a.name.localeCompare(b.name),
              )
              .map((item: MasterData) => ({
                name: item.name,
                value: item.id,
                label: item.name,
              })),
          });
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [businessId, connection],
  );

  // Stable silent refresh exposed to consumers.
  const reload = useCallback(() => loadContext(true), [loadContext]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  return (
    <BusinessContext.Provider
      value={{
        businessId,
        reload,
        select,
        roles,
        sites,
        masterData,
        states,
        zones,
        suburbs,
        area,
        collectionType,
        councils,
        sitesWithIds,
        getNameById,
      }}
    >
      {loading ? (
        <div className="flex items-center justify-center w-full h-screen">
          <Spin percent={Math.round(progress)} size="large" />
        </div>
      ) : (
        props.children
      )}
    </BusinessContext.Provider>
  );
};

export const useBusinessContext = () => useContext(BusinessContext);
